import { useContext, useEffect, useMemo, useRef, useState } from "react";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CircularProgress from "@mui/material/CircularProgress";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import HistoryIcon from "@mui/icons-material/History";
import SeleccionContext from "../../../../context/Seleccion/SeleccionContext";
import { useTableColumnPreference } from "../../../../hooks/useTableColumnPreference";
import { ICandidatoPendiente } from "../../../../interfaces/ISeleccion";
import { downloadFichaLegacyByRuts } from "../../../../services/fichaLegacyService";
import { estadosAConsultar } from "../../../../utils/estadosGestionIngreso";
import IconRegistroCandidato from "../../../ui/icons-seleccion/IconRegistroCandidato";
import IconBloquear from "../../../ui/icons-seleccion/IconBloquear";
import IconDescarte from "../../../ui/icons-seleccion/IconDescarte";
import ModalUpdateCandidate from "../../../ui/kit-mdtw/modals/modalUpdateCandidate/ModalUpdateCandidate";
import ModalGenerico from "../../../ui/kit-modal/ModalGenerico";
import ModalDescartarBloquear from "../../../ui/kit-modal/bloquearDescartar/ModalDescartarBloquear";
import FooterTablePagination from "../../../ui/kit-mdtw/table/FooterTablePagination";
import TableSkeleton from "../../../ui/kit-mdtw/table/TableSkeleton";
import Tooltip from "../../../ui/kit-mdtw/tooltip/Tooltip";
import NuevoCandidato from "../../../ui/groups/NuevoCandidato";
import PillValidacionCedula from "../../../ui/groups/PillValidacionCedula";
import PillEstadoGestionIngreso from "../../../ui/groups/PillEstadoGestionIngreso";
import FilterChips from "../../../ui/FilterChips";
import ActionMassive from "./ActionMassive";
import HistorialProceso from "./HistorialProceso";

// Columnas alternables (la de selección y la de acciones son fijas). El orden
// refleja el del encabezado y debe coincidir con las claves válidas del backend
// de preferencias (GESTION_INGRESO_MASIVO / GESTION_INGRESO_PROFESIONAL).
const COLUMN_DEFINITIONS = [
  { key: "estado", label: "Estado" },
  { key: "rut", label: "Rut" },
  { key: "nombre", label: "Nombre" },
  { key: "celular", label: "Celular" },
  { key: "reclutador", label: "Reclutador" },
  { key: "proceso", label: "ID Proceso" },
  { key: "cliente", label: "Cliente" },
  { key: "areaNegocio", label: "Área Negocio" },
  { key: "cargo", label: "Cargo" },
  { key: "validacion", label: "Validación" },
  { key: "asiste", label: "Asiste" },
] as const;

interface ITablaCandidatoGestionIngreso {
  /** "MASIVO" o "PROFESIONAL". */
  tipoProceso: string;
}

const restoreBodyScroll = (): void => {
  const body = document.querySelector("body");
  if (body) body.style.overflowY = "auto";
};

const TablaCandidatoGestionIngreso = ({ tipoProceso }: ITablaCandidatoGestionIngreso) => {
  const {
    listaCandidatoGestionIngreso,
    getCandidatoGestionIngreso,
    token,
    getRutVerificado,
    getDocument,
    putAsistenciaGestionIngreso,
  } = useContext(SeleccionContext);
  const esMasivo: boolean = tipoProceso === "MASIVO";
  const localStorageFilter: string = esMasivo
    ? "filtroGestionIngresoMasivo"
    : "filtroGestionIngresoProfesional";
  const [isLoadingTable, setIsLoadingTable] = useState<boolean | undefined>(undefined);
  const [isErrorTable, setIsErrorTable] = useState<boolean | undefined>(undefined);
  const [listadoSeleccionado, setListadoSeleccionado] = useState<string[]>([]);
  const [currentCandidates, setCurrentCandidates] = useState<string[]>([]);
  const [candidateSelect, setCandidateSelect] = useState<string[]>([]);
  const [isOpenModalBloquear, setIsOpenModalBloquear] = useState<boolean>(false);
  const [isOpenModalDescartar, setIsOpenModalDescartar] = useState<boolean>(false);
  // Asistencia marcada en pantalla mientras el backend confirma (busPerId -> asistio).
  const [asistencia, setAsistencia] = useState<Record<string, boolean>>({});
  const [guardandoAsistencia, setGuardandoAsistencia] = useState<string | null>(null);
  const [historialDe, setHistorialDe] = useState<{ busPerId: string; nombre: string } | null>(null);
  const [isOpenModalUpdateCandidate, setIsOpenModalUpdateCandidate] = useState<boolean>(false);
  const [selectedReclutadorId, setSelectedReclutadorId] = useState<string | undefined>(undefined);
  const [selectedReclutador, setSelectedReclutador] = useState<string | undefined>(undefined);
  const [, setResultBusperId] = useState<string | undefined>(undefined);
  const [, setButtonDisabled] = useState<boolean>(true);
  const [, setIsFree] = useState<boolean | undefined>(undefined);
  const [, setMessageSpanish] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingRut, setLoadingRut] = useState<string | null>(null);
  const [loadingFichaRut, setLoadingFichaRut] = useState<string | null>(null);
  const [offset, setOffset] = useState<any>({
    offset: { value: undefined, valid: true },
    pages: 1,
  });
  const [paginationData, setPaginationData] = useState<any>({});
  const [searchValue, setSearchValue] = useState<string>("");
  const searchRef = useRef<string>("");
  const [formFilter, setFormFilter] = useState<any>({
    estadoIngreso: [],
    nombre: [],
    rut: [],
    proceso: [],
    cliente: [],
    fechaPostulacion: [],
    tipoProceso: [tipoProceso],
    reclutador: [],
    cedula: [],
  });

  // Preferencia de columnas visibles (misma lógica que Por Validar).
  const loggedUserId = useMemo(() => sessionStorage.getItem("usuarioId") ?? "", []);
  const {
    visibleColumnKeys,
    setVisibleColumnKeys,
    isSaving: isSavingColumns,
    isSynchronized: isColumnsSynchronized,
  } = useTableColumnPreference({
    table: esMasivo ? "GESTION_INGRESO_MASIVO" : "GESTION_INGRESO_PROFESIONAL",
    storageNamespace: esMasivo ? "gestion-ingreso-masivo" : "gestion-ingreso-profesional",
    columnDefinitions: COLUMN_DEFINITIONS,
    loggedUserId,
  });
  const isCol = (key: string): boolean => visibleColumnKeys.includes(key as any);
  const visibleColCount = 2 + visibleColumnKeys.length; // checkbox + acciones fijas

  const handleSearch = (term: string): void => {
    searchRef.current = term;
    setSearchValue(term);
    setOffset((prev: any) => ({ ...prev, pages: 1 }));
  };

  const listRefresh = (): void => {
    if (!token) return;
    let filtros: any = { ...formFilter };
    const copyStorage: string | null = localStorage.getItem(localStorageFilter);
    if (copyStorage) {
      try {
        filtros = { ...JSON.parse(copyStorage) };
      } catch {
        filtros = { ...formFilter };
      }
    }
    // El tipo de proceso es el de la pestaña, no algo que el filtro pueda cambiar.
    filtros.tipoProceso = [tipoProceso];
    getCandidatoGestionIngreso({
      filtros: { ...filtros, search: searchRef.current },
      estados: estadosAConsultar(filtros.estadoIngreso),
      setIsErrorTable,
      setIsLoadingTable,
      setPaginationData,
      page: offset.pages ?? 1,
      limit: offset.offset.value?.code ?? 10,
    });
  };

  useEffect(() => {
    listRefresh();
  }, [token, localStorage.getItem(localStorageFilter), offset.pages, offset.offset, searchValue]);

  useEffect(() => {
    setAsistencia({});
  }, [listaCandidatoGestionIngreso]);

  const handleToggleAsistencia = async (busPerId: string, actual: boolean): Promise<void> => {
    if (guardandoAsistencia) return;
    const nuevo: boolean = !actual;
    setAsistencia((prev) => ({ ...prev, [busPerId]: nuevo }));
    setGuardandoAsistencia(busPerId);
    const ok: boolean = await putAsistenciaGestionIngreso({ busPerId, asistio: nuevo });
    if (!ok) setAsistencia((prev) => ({ ...prev, [busPerId]: actual }));
    setGuardandoAsistencia(null);
  };

  useEffect(() => {
    setCurrentCandidates(
      (listaCandidatoGestionIngreso || []).map((item: ICandidatoPendiente) => item.personaDNI)
    );
  }, [listaCandidatoGestionIngreso]);

  const handleClickAddOrRemoveChoise = (personaDNI: string): void => {
    setListadoSeleccionado((prev: string[]) =>
      prev.includes(personaDNI)
        ? prev.filter((item: string) => item !== personaDNI)
        : [...prev, personaDNI]
    );
  };

  const handleClickAddAll = (): void => {
    const todosMarcados: boolean =
      currentCandidates.length > 0 &&
      currentCandidates.every((personaDNI: string) => listadoSeleccionado.includes(personaDNI));
    setListadoSeleccionado(todosMarcados ? [] : [...currentCandidates]);
  };

  const handleDownloadFichaRow = async (personaDNI: string, busquedaId?: string, busPerId?: string): Promise<void> => {
    if (loadingFichaRut) return;
    setLoadingFichaRut(personaDNI);
    try {
      await downloadFichaLegacyByRuts([{ rut: personaDNI, busquedaId, busPerId }]);
    } catch (error) {
      console.log("ERROR DESCARGANDO FICHA", error);
    } finally {
      setLoadingFichaRut(null);
    }
  };

  const handleClickVerificarRut = (rowData: any): void => {
    const { personaDNI, reclutadorId, reclutador } = rowData;
    setCandidateSelect([personaDNI]);
    setSelectedReclutadorId(reclutadorId);
    setSelectedReclutador(reclutador);
    setLoadingRut(personaDNI);
    getRutVerificado({
      personaRut: personaDNI,
      setIsFree,
      setMessageSpanish,
      setLoading: (val: boolean) => { setIsLoading(val); if (!val) setLoadingRut(null); },
      setButtonDisabled,
      consultaRut: false,
      setIsOpenModalUpdateCandidate,
      token,
    });
    getDocument({ personaDNI, token });
  };

  const todosMarcados: boolean =
    currentCandidates.length > 0 &&
    currentCandidates.every((personaDNI: string) => listadoSeleccionado.includes(personaDNI));

  return (
    <>
      <NuevoCandidato getCandidates={listRefresh} />
      <section className="content-seleccion-multiple">
        <div className="content-multiple">
          <label className="title-multiple">
            <span
              className={`selects-badge${listadoSeleccionado.length === 0 ? " inactive" : ""}`}
              style={{ transition: "background 0.3s ease, color 0.3s ease, font-weight 0.3s ease" }}
            >
              {listadoSeleccionado.length > 0
                ? `${listadoSeleccionado.length} ítem${listadoSeleccionado.length !== 1 ? "s" : ""} seleccionado${listadoSeleccionado.length !== 1 ? "s" : ""}`
                : "0 ítems seleccionados"}
            </span>
          </label>
        </div>
      </section>
      <section
        className="content-seleccion-multiple action-in-line margin-top-20"
        style={{ flexDirection: "column", alignItems: "stretch" }}
      >
        <ActionMassive
          tipoProceso={tipoProceso}
          etapaSeleccion={esMasivo ? "Validados" : "Seleccionados"}
          listadoSeleccionado={listadoSeleccionado}
          setListadoSeleccionado={setListadoSeleccionado}
          localStorageFilter={localStorageFilter}
          formFilter={formFilter}
          setFormFilter={setFormFilter}
          listRefresh={listRefresh}
          searchValue={searchValue}
          onSearch={handleSearch}
          columnDefinitions={COLUMN_DEFINITIONS}
          visibleColumnKeys={visibleColumnKeys}
          setVisibleColumnKeys={(keys) => setVisibleColumnKeys(keys as any)}
          isSavingColumns={isSavingColumns}
          isColumnsSynchronized={isColumnsSynchronized}
        />
      </section>
      <section className="content-seleccion-multiple action-in-line">
        <FilterChips localStorageFilter={localStorageFilter} setFormFilter={setFormFilter} />
      </section>

      <section className="content-header-table">
        <label className="title-header-table">Gestión de Ingreso</label>
      </section>

      {!isLoadingTable ? (
        <>
          {isErrorTable ? (
            <div className="container-is-error-table">Error al cargar los datos.</div>
          ) : (
            <>
              <main className="container-table-list">
                <table className="content-table">
                  <thead className="thead-table-registros">
                    <tr>
                      <th className="th-table-registros col-w-check">
                        <button onClick={handleClickAddAll} className="button-select">
                          {todosMarcados ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                        </button>
                      </th>
                      {isCol("estado") && <th className="th-table-registros col-w-validacion">Estado</th>}
                      {isCol("rut") && <th className="th-table-registros col-w-rut">Rut</th>}
                      {isCol("nombre") && <th className="th-table-registros">Nombre</th>}
                      {isCol("celular") && <th className="th-table-registros">Celular</th>}
                      {isCol("reclutador") && <th className="th-table-registros">Reclutador</th>}
                      {isCol("proceso") && <th className="th-table-registros">ID Proceso</th>}
                      {isCol("cliente") && <th className="th-table-registros">Cliente</th>}
                      {isCol("areaNegocio") && (
                        <th className="th-table-registros">
                          Área <br></br>Negocio
                        </th>
                      )}
                      {isCol("cargo") && <th className="th-table-registros">Cargo</th>}
                      {isCol("validacion") && <th className="th-table-registros col-w-validacion">Validación</th>}
                      {isCol("asiste") && <th className="th-table-registros">Asiste</th>}
                      <th className="th-table-registros col-w-acciones">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="tbody-table-registros">
                    {(listaCandidatoGestionIngreso || []).length === 0 && (
                      <tr className="tr-table-registros">
                        <td className="td-table-registros" colSpan={visibleColCount}>
                          No hay candidatos en Gestión de Ingreso.
                        </td>
                      </tr>
                    )}
                    {(listaCandidatoGestionIngreso || []).map((item: ICandidatoPendiente) => {
                      const {
                        personaDNI,
                        reclutadorId,
                        primerNombre,
                        segundoNombre,
                        primerApellido,
                        segundoApellido,
                        clienteNombre,
                        areaNegocioCod,
                        cargoNombre,
                        fonoPrincipal,
                        asistio,
                        busquedaId,
                        busPerId,
                        busPerEstado,
                        reclutador,
                        veredictoCedula,
                        detalleCedula,
                      } = item;
                      return (
                        <tr
                          key={busPerId ?? personaDNI}
                          className={`tr-table-registros ${listadoSeleccionado.includes(personaDNI) && "checkeado"}`}
                        >
                          <td className="td-table-registros col-w-check">
                            <button
                              className="button-select"
                              onClick={() => handleClickAddOrRemoveChoise(personaDNI)}
                            >
                              {listadoSeleccionado.includes(personaDNI) ? (
                                <CheckBoxIcon />
                              ) : (
                                <CheckBoxOutlineBlankIcon />
                              )}
                            </button>
                          </td>
                          {isCol("estado") && (
                            <td className="td-table-registros col-w-validacion">
                              <PillEstadoGestionIngreso estado={busPerEstado} />
                            </td>
                          )}
                          {isCol("rut") && <td className="td-table-registros col-w-rut">{personaDNI}</td>}
                          {isCol("nombre") && (
                            <td className="td-table-registros">
                              {[primerNombre, segundoNombre, primerApellido, segundoApellido]
                                .filter(Boolean)
                                .join(" ")}
                            </td>
                          )}
                          {isCol("celular") && (
                            <td className="td-table-registros">{fonoPrincipal || "-"}</td>
                          )}
                          {isCol("reclutador") && <td className="td-table-registros">{reclutador}</td>}
                          {isCol("proceso") && <td className="td-table-registros">{busquedaId}</td>}
                          {isCol("cliente") && <td className="td-table-registros">{clienteNombre}</td>}
                          {isCol("areaNegocio") && (
                            <td className="td-table-registros">{areaNegocioCod || "-"}</td>
                          )}
                          {isCol("cargo") && <td className="td-table-registros">{cargoNombre || "-"}</td>}
                          {isCol("validacion") && (
                            <td className="td-table-registros col-w-validacion">
                              <PillValidacionCedula
                                veredictoCedula={veredictoCedula}
                                detalleCedula={detalleCedula}
                              />
                            </td>
                          )}
                          {isCol("asiste") && (() => {
                            const asiste: boolean = asistencia[busPerId] ?? asistio === true;
                            const guardando: boolean = guardandoAsistencia === busPerId;
                            return (
                              <td className="td-table-registros">
                                <Tooltip text={asiste ? "Asiste (clic para desmarcar)" : "No asiste (clic para marcar)"}>
                                  <button
                                    className="button-select"
                                    onClick={() => handleToggleAsistencia(busPerId, asiste)}
                                    disabled={guardando}
                                    aria-pressed={asiste}
                                    aria-label={asiste ? "Asiste" : "No asiste"}
                                  >
                                    {guardando ? (
                                      <CircularProgress size={18} thickness={4} sx={{ color: "#1e7a45" }} />
                                    ) : asiste ? (
                                      <CheckBoxIcon style={{ color: "#1e7a45" }} />
                                    ) : (
                                      <CheckBoxOutlineBlankIcon style={{ color: "#8a94a3" }} />
                                    )}
                                  </button>
                                </Tooltip>
                              </td>
                            );
                          })()}
                          <td className="td-table-registros col-w-acciones">
                            <section className="content-icon-table">
                              <Tooltip text="Ver Registro">
                                <button
                                  onClick={() => handleClickVerificarRut({ personaDNI, reclutadorId, reclutador })}
                                  className="button-content"
                                  disabled={loadingRut === personaDNI}
                                >
                                  {loadingRut === personaDNI ? (
                                    <CircularProgress size={18} thickness={4} sx={{ color: "#0065DD" }} />
                                  ) : (
                                    <IconRegistroCandidato fill={"#0065DD"} />
                                  )}
                                </button>
                              </Tooltip>
                              <Tooltip text={loadingFichaRut === personaDNI ? "Generando ficha…" : "Descargar Ficha"}>
                                <button
                                  onClick={() => handleDownloadFichaRow(personaDNI, busquedaId, busPerId)}
                                  className="button-content"
                                  disabled={loadingFichaRut === personaDNI}
                                >
                                  {loadingFichaRut === personaDNI ? (
                                    <CircularProgress size={18} thickness={4} sx={{ color: "#0065DD" }} />
                                  ) : (
                                    <PictureAsPdfIcon style={{ fontSize: 20, color: "#D32F2F" }} />
                                  )}
                                </button>
                              </Tooltip>
                              <Tooltip text="Historial">
                                <button
                                  onClick={() =>
                                    setHistorialDe({
                                      busPerId,
                                      nombre: `${primerNombre} ${primerApellido}`,
                                    })
                                  }
                                  className="button-content"
                                >
                                  <HistoryIcon style={{ fontSize: 21, color: "#48566B" }} />
                                </button>
                              </Tooltip>
                              <Tooltip text="Bloquear">
                                <button
                                  onClick={() => {
                                    setCandidateSelect([personaDNI]);
                                    setIsOpenModalBloquear(true);
                                  }}
                                  className="button-content"
                                >
                                  <IconBloquear fill={"#BE2A2A"} />
                                </button>
                              </Tooltip>
                              <Tooltip text="Descartar">
                                <button
                                  onClick={() => {
                                    setCandidateSelect([personaDNI]);
                                    setIsOpenModalDescartar(true);
                                  }}
                                  className="button-content"
                                >
                                  <IconDescarte fill={"#BE2A2A"} />
                                </button>
                              </Tooltip>
                            </section>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </main>
              <FooterTablePagination
                paginationData={paginationData}
                offset={offset}
                setOffset={setOffset}
              />
            </>
          )}
        </>
      ) : (
        <TableSkeleton cols={visibleColCount} />
      )}

      {isOpenModalUpdateCandidate && (
        <ModalUpdateCandidate
          title="Registro Candidato"
          subTitle="Todos los datos con (*) son obligatorios y deben ser completados antes de avanzar al candidato a la siguiente etapa del proceso."
          setIsOpenModalUpdateCandidate={setIsOpenModalUpdateCandidate}
          setResultBusperId={setResultBusperId}
          token={token}
          rut={candidateSelect[0]}
          reclutadorId={selectedReclutadorId}
          reclutador={selectedReclutador}
          isLoadingModal={isLoading}
          getCandidates={listRefresh}
          onChangeBack={() => {
            setIsOpenModalUpdateCandidate(false);
            restoreBodyScroll();
          }}
          onChangeGo={() => restoreBodyScroll()}
        />
      )}

      {historialDe && (
        <ModalGenerico
          title="HISTORIAL DEL CANDIDATO"
          width="w-70"
          subtitle={historialDe.nombre}
          onChangeBack={() => {
            setHistorialDe(null);
            restoreBodyScroll();
          }}
          buttonBackText="Cerrar"
        >
          <HistorialProceso busPerId={historialDe.busPerId} />
        </ModalGenerico>
      )}

      {isOpenModalBloquear && (
        <ModalGenerico
          title="BLOQUEO DE CANDIDATOS"
          width="w-70"
          subtitle="Estás por bloquear la siguiente selección de candidatos:"
          onChangeBack={() => {
            setIsOpenModalBloquear(false);
            restoreBodyScroll();
          }}
        >
          <ModalDescartarBloquear
            typeExclusion="bloqueo"
            getListRefresh={listRefresh}
            setListadoSeleccionado={setListadoSeleccionado}
            listSelect={listaCandidatoGestionIngreso ?? []}
            localStorageFilter={localStorageFilter}
            formFilter={formFilter}
            onChangeBack={() => {
              setIsOpenModalBloquear(false);
              restoreBodyScroll();
            }}
            list={candidateSelect}
          />
        </ModalGenerico>
      )}

      {isOpenModalDescartar && (
        <ModalGenerico
          title="DESCARTE DE CANDIDATOS"
          width="w-70"
          subtitle="Estás por descartar la siguiente selección de candidatos:"
          onChangeBack={() => {
            setIsOpenModalDescartar(false);
            restoreBodyScroll();
          }}
        >
          <ModalDescartarBloquear
            typeExclusion="descarte"
            getListRefresh={listRefresh}
            setListadoSeleccionado={setListadoSeleccionado}
            listSelect={listaCandidatoGestionIngreso ?? []}
            localStorageFilter={localStorageFilter}
            formFilter={formFilter}
            onChangeBack={() => {
              setIsOpenModalDescartar(false);
              restoreBodyScroll();
            }}
            list={candidateSelect}
          />
        </ModalGenerico>
      )}
    </>
  );
};

export default TablaCandidatoGestionIngreso;
